/**
 * Hook for managing Kanban board query operations
 * @remarks
 * Extracted from useKanbanBoard to reduce cognitive complexity.
 * Handles all data fetching for boards, phases, and cards.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useCurrentUser } from "@/features/auth";
import type { KanbanBoard, KanbanCard, KanbanPhase } from "@/generated/graphql";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import {
  CurrencyCode,
  KanbanSourceType,
  useGetKanbanBoardQuery,
  useListKanbanCardsQuery,
  useListKanbanPhasesQuery,
  useListMyKanbanBoardsQuery,
  useWatchlistListMyUserWatchlistsQuery,
} from "@/generated/graphql";
import {
  BOARDS_LOOKUP_PAGE_SIZE,
  CARDS_PAGE_SIZE,
} from "@/features/player-kanban/constants";
import {
  deriveKanbanCardsState,
  type KanbanCardsReadState,
} from "@/features/player-kanban/utils/kanbanCardsState";
import { resolveKanbanAccessDenial } from "@/features/player-kanban/utils/kanbanAccessDenial";

/**
 * SE-6491: bounded retry for a null specific-board read.
 * A read issued right after CreateKanbanBoard can land on a lagging read
 * replica and return `getKanbanBoard: null` with HTTP 200 — no GraphQL error,
 * so onError never fires and `cache-first` pins the miss. Without recovery the
 * screen dead-ends on a permanently blank board: the phases query stays
 * skipped and the default-phase auto-seed in useKanbanBoard never runs.
 */
const BOARD_READ_RETRY_LIMIT = 4;
/** Delay between null-read retries; replicas usually catch up within ~1s. */
const BOARD_READ_RETRY_DELAY_MS = 1_500;

/**
 * Builds an Apollo `onError` callback for a board-identity/content query
 * (specific board, boards list, phases): routes a confirmed permission
 * denial through the server's own wording, and falls back to a generic
 * retry-inviting message for every other failure.
 * @remarks SE-8534: shared by the three queries below (the fourth, cards,
 * intentionally does NOT use this — its onError is a diagnostic breadcrumb
 * only, see its own comment) so the same denial-vs-generic decision is made
 * once instead of re-derived per call site.
 * @param onError - The screen-level error setter this hook was given.
 * @param genericMessage - Fallback copy shown when the error is not a
 * permission denial.
 * @returns An Apollo `onError` callback.
 */
function createDenialAwareErrorHandler(
  onError: (message: string) => void,
  genericMessage: string
): (error: unknown) => void {
  return (error: unknown) => {
    onError(resolveKanbanAccessDenial(error) ?? genericMessage);
  };
}

/**
 * Return type for the useKanbanBoardQueries hook
 */
interface KanbanBoardQueriesResult {
  /** Current user's organization currency code */
  organizationCurrency: CurrencyCode;
  /** First watchlist ID for default board creation */
  firstWatchlistId: string | null;
  /** Current user ID */
  currentUserId: string | undefined;
  /** Current user's organization ID — part of the default-board identity key */
  organizationId: string | null | undefined;
  /** Active kanban board */
  board: KanbanBoard | null;
  /**
   * SE-6796: POSITIVE evidence that this account has no boards.
   *
   * True only when the board-list read was not skipped, is not in flight, did
   * not error, and returned a payload containing zero edges. A skipped,
   * in-flight, or errored read leaves this `false` — the absence of data is not
   * evidence of the absence of a board, and treating it as such is what minted
   * duplicate boards. Never substitute `!board` for this.
   */
  hasNoBoards: boolean;
  /** Kanban phases for the board */
  phases: KanbanPhase[];
  /** Kanban cards on the board */
  cards: KanbanCard[];
  /**
   * SE-8534: the backend's own denial message when the specific-board OR
   * boards-list read was REFUSED for lack of a `user_kanban_board` grant,
   * else null. Distinct from `cardsState`'s `denied` member (below) because
   * a denial can surface on the board identity read before the cards read
   * ever fires — this is what a screen-level "you don't have access to this
   * board" gate reads, without a retry affordance, per SE-7918's finding
   * that a denial is permanent, not transient.
   */
  accessDeniedMessage: string | null;
  /**
   * SE-7624: the cards read's resolution state as a discriminated union —
   * `loading | empty | failed | stale | ready` — instead of a nullable list.
   * `cards` above collapses `failed` and `empty` to the same `[]`, which is
   * exactly the ambiguity that let a failed read render as an empty board;
   * consumers that need to distinguish them must switch on this.
   */
  cardsState: KanbanCardsReadState;
  /** Whether any queries are loading */
  isLoading: boolean;
  /**
   * SE-5630: the board id whose CONTENT — phases and cards both — is currently
   * in hand, or `null` when none is.
   *
   * This is what separates a cold start from a background refresh, which
   * `isLoading` alone cannot do: it aggregates five queries and re-enters
   * `true` whenever any of them refetches. Non-null here means the surface has
   * something real on screen that a refresh must not throw away.
   *
   * It goes `null` on a board SWITCH by construction — Apollo reports
   * `data: undefined` for the new variables while their read is in flight — so
   * the previous board's columns are never shown under a new board's name.
   */
  loadedContentBoardId: string | null;
  /** Whether watchlist is loading */
  watchlistLoading: boolean;
  /** Whether phases are loading */
  phasesLoading: boolean;
  /** Whether specific board is loading */
  specificBoardLoading: boolean;
  /** Whether boards list is loading */
  boardsListLoading: boolean;
  /** Refetch phases function */
  refetchPhases: () => Promise<unknown>;
  /** Refetch cards function */
  refetchCards: () => Promise<unknown>;
  /** Refetch all board data (board, phases, cards) — used by the loading-timeout retry */
  refetchAll: () => Promise<unknown>;
  /** Load next page of kanban cards */
  loadMoreCards: () => void;
}

/**
 * Hook for fetching kanban board data
 * @param boardId - Optional board ID to load
 * @param onError - Error handler callback
 * @returns Object with board, phases, cards data, loading states, refetch functions, and loadMoreCards for pagination
 */
const useKanbanBoardQueries = (
  boardId: string | null | undefined,
  onError: (message: string) => void
): KanbanBoardQueriesResult => {
  // Authentication and user data
  const currentUser = useCurrentUser();
  const organizationCurrency =
    currentUser?.organization?.currencyCode || CurrencyCode.Usd;

  // Get user's watchlists for board creation
  const {
    data: watchlistData,
    loading: watchlistLoading,
    refetch: refetchWatchlists,
  } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    fetchPolicy: "cache-first",
  });

  const firstWatchlistId = useMemo(() => {
    const firstWatchlist =
      watchlistData?.listMyUserWatchlists?.edges?.[0]?.node?.watchlist;
    return firstWatchlist?.id || null;
  }, [watchlistData?.listMyUserWatchlists?.edges]);

  // Load specific board by ID
  const {
    data: specificBoardData,
    loading: specificBoardLoading,
    error: specificBoardError,
    refetch: refetchSpecificBoard,
  } = useGetKanbanBoardQuery({
    variables: { id: boardId! },
    skip: !boardId,
    fetchPolicy: "cache-first",
    // SE-8534: a permission denial gets the backend's own wording — never
    // the generic "please try again", which actively invites the retry
    // storm SE-7918 measured (a denial cannot be healed by retrying it).
    onError: createDenialAwareErrorHandler(
      onError,
      "Failed to load board. Please try again."
    ),
  });

  // SE-8534: the specific-board read was REFUSED for lack of a
  // `user_kanban_board` grant — a permanent condition, never a transient one
  // worth retrying. Computed unconditionally (not just in the onError above)
  // because `errorPolicy: "all"` resolves the query with `error` set rather
  // than rejecting, so this is reachable on every render, not just the first.
  //
  // `getKanbanBoard == null` is load-bearing, not redundant with the error
  // check inside `resolveKanbanAccessDenial`: under `errorPolicy: "all"` a
  // FORBIDDEN error can in principle arrive on `error` while a DIFFERENT,
  // sibling field on this same query still resolved data (the root
  // `getKanbanBoard` non-null). Only treat the read as a board-level denial
  // when the root field itself carries no board.
  const boardAccessDeniedMessage =
    specificBoardData?.getKanbanBoard == null
      ? resolveKanbanAccessDenial(specificBoardError)
      : null;

  // SE-6491: a completed specific-board read whose payload is null is a
  // transient replica miss — the retry effect (in the side-effects section
  // below) refetches it, bounded. `=== null` is deliberate: undefined covers
  // skipped/in-flight reads.
  //
  // SE-8534: `!boardAccessDeniedMessage` is load-bearing. A permission-denied
  // read ALSO resolves `getKanbanBoard: null` (the denied field resolves to
  // null alongside a GraphQL error under `errorPolicy: "all"`), which — before
  // this guard — was indistinguishable from the transient replica-lag miss
  // this retry exists to heal, so a permanently denied board was
  // auto-refetched up to BOARD_READ_RETRY_LIMIT times on every mount.
  const boardReadMissed =
    !!boardId &&
    !specificBoardLoading &&
    !boardAccessDeniedMessage &&
    specificBoardData?.getKanbanBoard === null;
  const boardReadAttemptRef = useRef(0);
  const boardReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boardReadCancelledRef = useRef(false);

  // Load available boards (fallback when no specific boardId was requested).
  // SE-6796: BOARDS_LOOKUP_PAGE_SIZE rows, not one — see the constant's doc for
  // why a single-row window is not enough to resolve the intended board.
  const boardsListSkipped = !!boardId || watchlistLoading;
  const {
    data: boardsListData,
    loading: boardsListLoading,
    error: boardsListError,
    refetch: refetchBoardsList,
  } = useListMyKanbanBoardsQuery({
    variables: { first: BOARDS_LOOKUP_PAGE_SIZE },
    skip: boardsListSkipped,
    fetchPolicy: "cache-first",
    // SE-8534: same reasoning as the specific-board read above.
    onError: createDenialAwareErrorHandler(
      onError,
      "Failed to load boards. Please try again."
    ),
  });

  const boardsListEdges = boardsListData?.listMyKanbanBoards?.edges;
  // SE-8534: surfaced alongside boardAccessDeniedMessage below so a denial on
  // EITHER the specific-board read or its boards-list fallback stops the
  // screen from rendering a generic error with a doomed retry.
  const boardsListAccessDeniedMessage =
    resolveKanbanAccessDenial(boardsListError);

  // SE-6796: the board-list read SETTLED SUCCESSFULLY and produced a payload.
  // Each conjunct rules out a state that is indistinguishable from "empty" if
  // you only look at the derived board: skipped (never asked), loading (asked,
  // no answer yet), errored (asked, answer lost), payload absent (no data to
  // draw any conclusion from).
  const boardsListSettled =
    !boardsListSkipped &&
    !boardsListLoading &&
    !boardsListError &&
    boardsListEdges !== undefined;

  const hasNoBoards = boardsListSettled && boardsListEdges.length === 0;

  // Determine active board.
  // SE-6796: when falling back to the list, prefer the WATCHLIST board bound to
  // the same watchlist the auto-create would use. Taking `edges[0]` made the
  // screen's landing board depend on wherever the intended board happened to
  // sort, which in a large org is arbitrary. The `edges[0]` fallback is kept so
  // an account whose only boards are of another kind still lands somewhere.
  const board: KanbanBoard | null = useMemo(() => {
    if (boardId) {
      return (specificBoardData?.getKanbanBoard || null) as KanbanBoard | null;
    }
    const edges = boardsListData?.listMyKanbanBoards?.edges;
    if (!edges?.length) {
      return null;
    }
    const intended = firstWatchlistId
      ? edges.find(
          edge =>
            edge.node.sourceType === KanbanSourceType.Watchlist &&
            edge.node.sourceId === firstWatchlistId
        )
      : undefined;
    return (intended?.node ?? edges[0]?.node ?? null) as KanbanBoard | null;
  }, [boardId, specificBoardData, boardsListData, firstWatchlistId]);

  // Phase queries
  const {
    data: phasesData,
    loading: phasesLoading,
    refetch: refetchPhases,
  } = useListKanbanPhasesQuery({
    variables: { kanbanBoardId: board?.id! },
    skip: !board?.id,
    fetchPolicy: "cache-first",
    // SE-8534: same reasoning as the specific-board read above.
    onError: createDenialAwareErrorHandler(
      onError,
      "Failed to load phases. Please try again."
    ),
  });

  const phases: KanbanPhase[] = useMemo(
    () => (phasesData?.listKanbanPhases || []) as KanbanPhase[],
    [phasesData]
  );

  const cardsBoardId = board?.id;

  // Card queries — CARDS_PAGE_SIZE is shared with useTagMutations and
  // usePlayerCardId so all call sites produce the same Apollo cache key.
  const {
    data: cardsData,
    loading: cardsLoading,
    error: cardsError,
    refetch: refetchCards,
    fetchMore: cardsFetchMore,
  } = useListKanbanCardsQuery({
    variables: { kanbanBoardId: cardsBoardId ?? "", first: CARDS_PAGE_SIZE },
    skip: !cardsBoardId,
    fetchPolicy: "cache-first",
    onError: (error: unknown) => {
      // SE-7624: this used to be the ONLY handling of a failed cards read —
      // visible only to whoever has devtools open, which is nobody in
      // production. `error` above is now bound and feeds `cardsState`; this
      // stays as a diagnostic breadcrumb, not the error handling itself.
      console.warn("[useKanbanBoardQueries] cards query failed", error);
    },
  });

  const cards: KanbanCard[] = useMemo(
    () =>
      (cardsData?.listKanbanCards?.edges?.map(edge => ({
        ...edge.node.kanbanCard,
        player: edge.node.player,
      })) as KanbanCard[]) || [],
    [cardsData]
  );

  // SE-7624: the cards read is "pending" — an absent result is not evidence
  // of zero cards — while the query is skipped for want of a board id, or
  // while it is genuinely in flight.
  const cardsIsPending = !cardsBoardId || cardsLoading;

  // SE-8534: the cards read itself can be denied independently of the board
  // identity read above (e.g. a grant revoked between the board read and the
  // cards read). Feeding this into `cardsState` is what turns a permission
  // denial into the `denied` state instead of `failed`, which is what drops
  // the doomed retry affordance for this specific read.
  //
  // `listKanbanCards == null` guards this the same way `boardAccessDeniedMessage`
  // above does: only classify the read as denied when the root field itself
  // carries no data, so a FORBIDDEN error on an unrelated sibling field under
  // `errorPolicy: "all"` cannot misclassify a genuinely-loaded card list.
  const cardsAccessDeniedMessage =
    cardsData?.listKanbanCards == null
      ? resolveKanbanAccessDenial(cardsError)
      : null;

  const cardsState: KanbanCardsReadState = useMemo(
    () =>
      deriveKanbanCardsState({
        cards,
        isPending: cardsIsPending,
        hasError: Boolean(cardsError),
        accessDeniedMessage: cardsAccessDeniedMessage,
      }),
    [cards, cardsIsPending, cardsError, cardsAccessDeniedMessage]
  );

  // SE-8534: the single "you don't have access to this board" signal a
  // screen gates on, ahead of the cards-specific `cardsState.denied` above —
  // whichever board-identity read (specific board, or its boards-list
  // fallback) was denied first is reported, since either one means the
  // screen has no board to show at all.
  const accessDeniedMessage =
    boardAccessDeniedMessage ?? boardsListAccessDeniedMessage ?? null;

  // Aggregate loading state
  const isLoading =
    specificBoardLoading ||
    boardsListLoading ||
    phasesLoading ||
    cardsLoading ||
    watchlistLoading;

  // SE-5630: which board the content on screen actually belongs to.
  //
  // Both payloads are required: a board with phases but no cards yet renders
  // empty columns, which is not a board worth protecting from a skeleton. A
  // board with zero cards still satisfies this — `cardsData` is an object
  // holding an empty edge list, which is a real answer, not a missing one.
  // Presence is read as truthiness rather than `!== undefined` so a `null`
  // payload (which is what a settled-but-empty read can deliver under the
  // client-wide `errorPolicy: "all"`) counts as nothing in hand.
  const loadedContentBoardId =
    cardsBoardId && phasesData && cardsData ? cardsBoardId : null;

  // Re-issue every active board query. Used by the loading-timeout retry so a
  // stuck/never-resolving load (e.g. a backend query that hangs without ever
  // erroring, or a transient complexity-limit 400) can actually recover —
  // previously the retry only reset UI state and never re-fetched, so the
  // board re-entered the same 45s timeout indefinitely (SE-5237).
  const refetchAll = useCallback(async () => {
    const tasks: Promise<unknown>[] = [
      refetchWatchlists(),
      boardId ? refetchSpecificBoard() : refetchBoardsList(),
      ...(board?.id ? [refetchPhases(), refetchCards()] : []),
    ];
    // Surface nothing on failure — each query's own onError already reports;
    // the retry just needs to re-arm the in-flight requests.
    return Promise.allSettled(tasks);
  }, [
    boardId,
    board?.id,
    refetchWatchlists,
    refetchSpecificBoard,
    refetchBoardsList,
    refetchPhases,
    refetchCards,
  ]);

  // Eagerly fetch all card pages so the board renders completely.
  // Uses a ref-based guard that prevents re-entry during fetch-all,
  // but resets when a refetch replaces cached data (detected by edge
  // count dropping) so remaining pages can be re-fetched.
  const fetchMoreRef = useRef(cardsFetchMore);
  const hasFetchedAllPagesRef = useRef(false);
  const lastFetchedBoardIdRef = useRef<string | null>(null);
  const prevEdgeCountRef = useRef(0);
  const currentEdgeCount = cardsData?.listKanbanCards?.edges?.length ?? 0;

  useEffect(() => {
    fetchMoreRef.current = cardsFetchMore;
  }, [cardsFetchMore]);

  // SE-6491: reset the null-read retry budget when the target board changes.
  useEffect(() => {
    boardReadAttemptRef.current = 0;
  }, [boardId]);

  // SE-6491: bounded self-scheduling retry for a null specific-board read.
  // Each attempt refetches and, still-missed, arms the next timer from the
  // refetch's completion; resolving the board flips `boardReadMissed` and the
  // cleanup cancels the chain.
  useEffect(() => {
    if (!boardReadMissed) {
      return undefined;
    }
    boardReadCancelledRef.current = false;
    const scheduleRetry = () => {
      if (
        boardReadCancelledRef.current ||
        boardReadAttemptRef.current >= BOARD_READ_RETRY_LIMIT
      ) {
        return;
      }
      boardReadTimerRef.current = setTimeout(() => {
        boardReadAttemptRef.current += 1;
        void refetchSpecificBoard().finally(scheduleRetry);
      }, BOARD_READ_RETRY_DELAY_MS);
    };
    scheduleRetry();
    return () => {
      boardReadCancelledRef.current = true;
      if (boardReadTimerRef.current !== null) {
        clearTimeout(boardReadTimerRef.current);
      }
    };
    // boardId is a dependency so switching boards cancels the previous
    // timer chain and starts a fresh retry cycle for the new board.
  }, [boardReadMissed, refetchSpecificBoard, boardId]);

  useEffect(() => {
    // Reset guard when board changes
    if (cardsBoardId !== lastFetchedBoardIdRef.current) {
      hasFetchedAllPagesRef.current = false;
      lastFetchedBoardIdRef.current = cardsBoardId ?? null;
      prevEdgeCountRef.current = 0;
    }

    const pageInfo = cardsData?.listKanbanCards?.pageInfo;

    // Detect refetch: edge count dropped while hasNextPage is true.
    // This means refetchQueries replaced cached data with page 1 only —
    // reset the guard so remaining pages can be re-fetched.
    if (currentEdgeCount < prevEdgeCountRef.current && pageInfo?.hasNextPage) {
      hasFetchedAllPagesRef.current = false;
    }
    prevEdgeCountRef.current = currentEdgeCount;

    if (
      !pageInfo?.hasNextPage ||
      !pageInfo.endCursor ||
      hasFetchedAllPagesRef.current
    ) {
      return;
    }

    // Set synchronously BEFORE any async work — prevents re-entry
    hasFetchedAllPagesRef.current = true;

    const fetchAllPages = async (cursor: string): Promise<void> => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- fetchMore returns untyped result
        const result: any = await fetchMoreRef.current({
          variables: { after: cursor },
        });
        const nextPageInfo = result?.data?.listKanbanCards?.pageInfo;
        if (nextPageInfo?.hasNextPage && nextPageInfo.endCursor) {
          await fetchAllPages(nextPageInfo.endCursor);
        }
      } catch (error) {
        console.warn("[useKanbanBoardQueries] fetchAllPages failed", error);
      }
    };

    fetchAllPages(pageInfo.endCursor);
  }, [cardsData?.listKanbanCards?.pageInfo, cardsBoardId, currentEdgeCount]);

  return {
    organizationCurrency,
    firstWatchlistId,
    currentUserId: currentUser?.id,
    organizationId: currentUser?.organizationId,
    board,
    hasNoBoards,
    accessDeniedMessage,
    phases,
    cards,
    cardsState,
    isLoading,
    loadedContentBoardId,
    watchlistLoading,
    phasesLoading,
    specificBoardLoading,
    boardsListLoading,
    refetchPhases,
    refetchCards,
    refetchAll,
    loadMoreCards: () => {},
  };
};

export default useKanbanBoardQueries;
